// PatrolPages.tsx — three LIFF pages in the /liff/patrol/* family.
//
// /liff/patrol/today  → tenant_admin / manager / auditor see a daily roll-up
//                       across all sites (dispatch from manager_today data
//                       until patrol-api ships a per-tenant aggregation).
// /liff/patrol/live   → tenant_admin / manager — real-time view (placeholder
//                       with the operational layout we'll fill once the
//                       live-feed worker pushes via SSE).
// /liff/patrol/scan   → officer / tenant_admin — NFC scan launcher; the
//                       button calls liff.scanCodeV2() if available so an
//                       officer can read an NTAG424 tag without leaving LINE.

import React, { useEffect, useMemo, useState } from "react";
import liff from "@line/liff";
import { useLiff } from "../../../liff/index.js";
import { apiFetch } from "../../../lib/api-client.js";
import { fetchManagerToday, type ManagerTodayData } from "../../../liff/api.js";
import {
  MobileShell, Card, CardHeader, MetricGrid, ProgressBar, MobileSpinner,
  EmptyMobile, ComingSoonCard, ListRow, PrimaryButton, SecondaryButton,
} from "../../../liff/components/Mobile.js";

/** /liff/patrol/today — daily aggregate across the tenant's sites. */
export function PatrolTodayPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<ManagerTodayData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    fetchManagerToday(session.tenant_id, session.session_token)
      .then(setData)
      .catch((e) => setError((e as Error).message ?? "load_failed"));
  }, [session]);

  if (error) return (
    <MobileShell title="巡邏狀態" subtitle="今日">
      <Card><p className="text-sm text-danger">無法載入：{error}</p></Card>
    </MobileShell>
  );
  if (!data) return <MobileSpinner message="正在載入今日巡邏狀態" />;

  const { totals, sites } = data;
  const sortedByOverdue = [...sites].sort((a, b) => b.overdue_count - a.overdue_count);

  return (
    <MobileShell title="巡邏狀態" subtitle={`${data.date_local} · ${totals.sites_active} 個案場`}>
      <Card>
        <CardHeader title="今日完成率" hint={`${totals.sites_active} 案場`} />
        <p className="text-3xl font-semibold mb-3">
          {Math.round(totals.completion_rate_avg * 100)}
          <span className="text-base text-text-secondary">%</span>
        </p>
        <ProgressBar value={totals.completion_rate_avg} />
      </Card>

      <MetricGrid items={[
        { label: "在勤案場",   value: String(totals.sites_active) },
        { label: "漏巡點位",   value: String(totals.overdue_count),  tone: totals.overdue_count > 0 ? "danger" : "success" },
        { label: "今日事件",   value: String(totals.incidents_count), tone: "warning" },
        { label: "未結案件",   value: String(totals.open_incidents_count), tone: totals.open_incidents_count > 0 ? "danger" : "success" },
      ]} />

      <Card>
        <CardHeader title="案場狀態" hint="按漏巡排序" />
        {sortedByOverdue.length === 0 ? <EmptyMobile title="今日無進行中案場" /> : (
          <ul className="space-y-2">
            {sortedByOverdue.map((s) => (
              <li key={s.site_id} className="rounded-xl bg-bg-raised p-3">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <p className="text-sm font-medium truncate">{s.site_name}</p>
                  <span className={`text-[10px] font-semibold uppercase ${s.risk === "low" ? "text-success" : s.risk === "med" ? "text-warning" : "text-danger"}`}>{s.risk}</span>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-text-muted mb-1.5">
                  <span>{Math.round(s.completion_rate * 100)}%</span>
                  {s.overdue_count > 0 && <span className="text-danger">漏 {s.overdue_count}</span>}
                  {s.incidents_count > 0 && <span className="text-warning">事件 {s.incidents_count}</span>}
                </div>
                <ProgressBar value={s.completion_rate} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </MobileShell>
  );
}

/** /liff/patrol/live — placeholder with the layout the live worker will fill. */
export function PatrolLivePage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<ManagerTodayData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    fetchManagerToday(session.tenant_id, session.session_token)
      .then(setData)
      .catch((e: unknown) => setError((e as Error).message));
  }, [session]);

  return (
    <MobileShell title="巡邏監控" subtitle="即時案場狀態">
      <Card className="!p-3">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs">
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            即時連線
          </span>
          <span className="text-[10px] text-text-muted uppercase">LIVE</span>
        </div>
      </Card>

      {error && (
        <Card><p className="text-sm text-danger">無法載入：{error}</p></Card>
      )}

      {data && (
        <Card>
          <CardHeader title="目前在勤" hint={`${data.totals.sites_active} 案場`} />
          <ul className="space-y-2">
            {data.sites.slice(0, 6).map((s) => (
              <li key={s.site_id} className="rounded-xl bg-bg-raised p-3 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{s.site_name}</p>
                  <p className="text-[11px] text-text-muted">完成 {Math.round(s.completion_rate * 100)}% · 風險 {s.risk}</p>
                </div>
                <span className={`text-[10px] font-semibold uppercase ${s.risk === "low" ? "text-success" : s.risk === "med" ? "text-warning" : "text-danger"}`}>{s.risk}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <ComingSoonCard
        title="即時推播"
        description="即時逐分鐘更新地點位狀態，含 GPS 軌跡、滯留警告與離線警示。"
        willShow={[
          "每名巡邏員的當前案場與最後打卡時間",
          "未在窗口內打卡的點位即時警示",
          "案場 GPS 軌跡疊加（最近 60 分鐘）",
          "斷線重連自動補抓上次未顯示的事件",
        ]}
      />
    </MobileShell>
  );
}

/** /liff/patrol/scan — wraps liff.scanCodeV2() so officers can read NTAG424 tags inside LINE. */
interface RecentScanRow {
  id: string;
  location_id: string | null;
  location_label: string | null;
  resolved_kind: "sdm_url" | "ntag_uid" | "location_code" | "unknown" | string;
  scanned_value: string | null;
  verified: boolean;
  scanned_at: string;
}

export function PatrolScanPage(): React.ReactElement {
  const { session } = useLiff();
  const [scanResult, setScanResult] = useState<{ value: string; resolved: { kind: string; location_label: string | null; site_name: string | null } | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState<RecentScanRow[] | null>(null);
  const [reload, setReload] = useState(0);

  // LIFF scanCodeV2 is in-client only; web fallback returns NotSupported.
  const supported = useMemo(() => {
    try { return liff.isInClient() && typeof liff.scanCodeV2 === "function"; } catch { return false; }
  }, []);

  useEffect(() => {
    if (!session?.session_token) return;
    apiFetch(`/v1/patrols/scan/recent`, {}, { jwt: session.session_token })
      .then(async (r) => { if (r.ok) setRecent(((await r.json()) as { items: RecentScanRow[] }).items); })
      .catch(() => { /* keep null, "尚無資料" 顯示 */ });
  }, [session?.session_token, reload]);

  async function startScan(): Promise<void> {
    if (!session?.session_token) return;
    setError(null);
    setBusy(true);
    try {
      const sdkResult = await liff.scanCodeV2();
      const value = sdkResult.value ?? "";
      if (!value) {
        setError("掃描內容為空，請再試一次。");
        return;
      }
      // POST to patrol-api so the scan is logged in audit_logs.
      const r = await apiFetch(
        `/v1/patrols/scan`,
        { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ scanned_value: value }) },
        { jwt: session.session_token },
      );
      if (r.ok) {
        const body = (await r.json()) as { resolved_kind: string; location_label: string | null; site_name: string | null };
        setScanResult({
          value,
          resolved: { kind: body.resolved_kind, location_label: body.location_label, site_name: body.site_name },
        });
        setReload((n) => n + 1);
      } else {
        const text = await r.text().catch(() => `HTTP ${r.status}`);
        setError(text.slice(0, 200));
        setScanResult({ value, resolved: null });
      }
    } catch (e) {
      setError((e as Error).message ?? "scan_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <MobileShell title="巡邏掃描" subtitle="加密巡邏標籤 / QR Code">
      <Card>
        <CardHeader title="掃描狀態" hint={supported ? "可掃描" : "不支援"} />
        <p className="text-sm text-text-secondary mb-4 leading-relaxed">
          將手機靠近 NFC 標籤或對準 QR Code 即可記錄到站。掃描後系統會留下稽核紀錄；
          加密巡邏標籤驗證將在後續版本上線。
        </p>
        {!supported && (
          <p className="text-xs text-warning bg-warning/10 rounded-lg p-3 mb-3">
            您目前不在 LINE 應用程式內，掃描功能須從 LINE 內開啟此頁。
          </p>
        )}
        {scanResult && (
          <div className="rounded-xl bg-bg-raised p-3 mb-3 space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-text-muted">已記錄</p>
            {scanResult.resolved?.location_label ? (
              <p className="text-sm font-medium">{scanResult.resolved.location_label}{scanResult.resolved.site_name ? ` · ${scanResult.resolved.site_name}` : ""}</p>
            ) : (
              <p className="text-sm text-text-secondary">無法對位到地點，已留稽核紀錄。</p>
            )}
            <p className="text-[10px] text-text-muted">
              類型：{kindLabel(scanResult.resolved?.kind)}
            </p>
            <p className="text-[10px] text-text-muted font-mono break-all">{scanResult.value}</p>
          </div>
        )}
        {error && (
          <p className="text-xs text-danger bg-danger/10 rounded-lg p-3 mb-3">{error}</p>
        )}
        <PrimaryButton onClick={startScan} disabled={!supported || busy || !session}>
          {busy ? "掃描中…" : "開始掃描"}
        </PrimaryButton>
        <div className="h-2" />
        <SecondaryButton href="/liff/incident/new">回報異常</SecondaryButton>
      </Card>

      <Card>
        <CardHeader title="近期掃描" hint={recent ? `${recent.length} 筆` : "—"} />
        {recent === null ? (
          <p className="text-sm text-text-muted">載入中…</p>
        ) : recent.length === 0 ? (
          <p className="text-sm text-text-muted">尚無掃描紀錄。掃描後會自動出現在這裡。</p>
        ) : (
          <ul className="space-y-2">
            {recent.slice(0, 10).map((r) => (
              <li key={r.id} className="rounded-xl bg-bg-raised p-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{r.location_label ?? "未對位"}</span>
                  <span className="text-text-muted font-mono">{r.scanned_at.slice(11, 16)}</span>
                </div>
                <p className="text-[10px] text-text-muted">{kindLabel(r.resolved_kind)}{r.verified ? " · 已驗證" : " · 未驗證"}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {session && (
        <p className="text-[10px] text-text-muted text-center">
          以 {session.role} 身份登入 · tenant {session.tenant_id.slice(0, 8)}…
        </p>
      )}
    </MobileShell>
  );
}

function kindLabel(k: string | undefined | null): string {
  switch (k) {
    case "sdm_url":         return "加密巡邏標籤 URL";
    case "ntag_uid":        return "加密巡邏標籤 UID";
    case "location_code": return "QR / 代碼";
    case "unknown":
    case undefined:
    case null:
    case "":                return "未知格式";
    default:                return String(k);
  }
}
